/**
 * Die Statistik-Ansicht.
 *
 * Die Balken sind aus CSS gebaut, nicht aus einer Diagrammbibliothek: Es geht
 * um 25 Jahre und 13 Tiefenklassen. Dafuer noch ein Rahmenwerk zu laden, waere
 * Gewicht ohne Gegenwert.
 *
 * Die Tiefenklassen tragen die Farbe ihrer Tiefe — dieselbe Skala wie Liste und
 * Profil. Ein Blick auf das Histogramm zeigt damit sofort, in welchem Bereich
 * der Bestand liegt.
 */

import { dauer, monatJahr, tiefe, temperatur } from '../format.js';
import { berechne, nachBuddy, nachReise } from '../statistik.js';
import { beschreibung, filtere, istLeer, zuHash } from '../suche.js';
import { tiefenfarbe } from '../tiefenfarbe.js';
import { symbolHtml } from '../symbole.js';
import { kennzahl } from './kennzahl.js';

const de = new Intl.NumberFormat('de-CH');

function verweis(tg, text) {
  return tg ? `<a href="#/tauchgang/${tg.id}">${text}</a>` : '—';
}

function jahresbalken(werte, kriterien) {
  if (werte.length === 0) return '';
  const hoechste = Math.max(...werte.map(([, anzahl]) => anzahl));
  return `<div class="balken jahre">${werte.map(([jahr, anzahl]) => {
    const hoehe = Math.max(2, Math.round((anzahl / hoechste) * 100));
    const ziel = zuHash({ ...kriterien, jahr });
    return `<a class="balken-saeule" href="${ziel}" title="${jahr}: ${anzahl} Tauchgänge">
      <span class="balken-zahl">${anzahl}</span>
      <span class="balken-koerper" style="height: ${hoehe}%"></span>
      <span class="balken-marke">${jahr.slice(2)}</span>
    </a>`;
  }).join('')}</div>`;
}

function tiefenbalken(klassen, kriterien) {
  if (klassen.length === 0) return '';
  const hoechste = Math.max(...klassen.map((k) => k.anzahl));
  return `<div class="balken tiefen">${klassen.map((k) => {
    const hoehe = Math.max(2, Math.round((k.anzahl / hoechste) * 100));
    const farbe = tiefenfarbe(k.von + 2.5);
    const ziel = zuHash({ ...kriterien, tiefeVon: k.von, tiefeBis: k.bis ?? null });
    const beschriftung = k.bis === null ? `${k.von}+` : `${k.von}`;
    return `<a class="balken-saeule" href="${ziel}"
       title="${k.von}–${k.bis ?? '∞'} m: ${k.anzahl} Tauchgänge">
      <span class="balken-zahl">${k.anzahl || ''}</span>
      <span class="balken-koerper" style="height: ${hoehe}%; background: ${farbe}"></span>
      <span class="balken-marke">${beschriftung}</span>
    </a>`;
  }).join('')}</div>`;
}

const SCHLUESSEL_OFFEN = 'tauchlogbuch.statistik.offen';

/** Welche Bloecke zuletzt offen waren — der Zustand ueberlebt den Reiterwechsel. */
function offeneBloecke() {
  try {
    const roh = JSON.parse(localStorage.getItem(SCHLUESSEL_OFFEN) ?? '[]');
    return new Set(Array.isArray(roh) ? roh : []);
  } catch {
    return new Set();
  }
}

function merkeOffen(wurzel) {
  const offen = [...wurzel.querySelectorAll('.klappe[open]')].map((k) => k.dataset.block);
  try {
    localStorage.setItem(SCHLUESSEL_OFFEN, JSON.stringify(offen));
  } catch {
    // Ein privater Reiter darf nichts merken — das ist kein Fehler.
  }
}

/**
 * Ein Block, der zu ist, bis jemand ihn braucht.
 *
 * Die Kurzfassung in der Zeile ist die eigentliche Arbeit: Sie muss die Frage
 * beantworten, ob sich das Aufklappen lohnt. Ein Block, dessen Zeile nur den
 * Titel traegt, ist eine Ueberschrift mit Zusatzklick.
 */
function klappe(schluessel, titel, kurz, inhalt, offen, zeichen = '') {
  return `<details class="klappe" data-block="${schluessel}"${offen ? ' open' : ''}>
    <summary>${zeichen ? `<span class="klappe-symbol">${symbolHtml(zeichen, 20)}</span>` : ''}
      <span class="klappe-titel">${titel}</span>
      <span class="klappe-kurz">${kurz}</span></summary>
    <div class="klappe-inhalt">${inhalt}</div>
  </details>`;
}

/** Exportiert, damit die drei Faelle ohne DOM pruefbar sind. „Mär 2019", „Mär–Apr 2019" oder „Dez 2018 – Jan 2019". */
export function zeitraum(von, bis) {
  if (!von) return '';
  if (!bis || von === bis) return monatJahr(von);
  const [jahrVon, monatVon] = von.split('-');
  const [jahrBis, monatBis] = bis.split('-');
  if (jahrVon === jahrBis && monatVon === monatBis) return monatJahr(von);
  if (jahrVon === jahrBis) return `${monatJahr(von).split(' ')[0]}–${monatJahr(bis)}`;
  return `${monatJahr(von)} – ${monatJahr(bis)}`;
}

export function jahresspanne(von, bis) {
  if (!von) return '';
  const a = von.slice(0, 4);
  const b = (bis ?? von).slice(0, 4);
  return a === b ? a : `${a}–${b}`;
}

function rangzeile(ziel, name, zusatz, anzahl) {
  return `<li><a href="${ziel}">
    <span class="rang-name">${name}${zusatz ? ` <span class="leise">${zusatz}</span>` : ''}</span>
    <span class="rang-zahl">${anzahl}</span>
  </a></li>`;
}

export function ansichtStatistik(alle, plaetze, kriterien = {}) {
  const auswahl = filtere(alle, kriterien);
  const s = berechne(auswahl, plaetze);
  const wurzel = document.createElement('section');
  wurzel.className = 'statistik';

  if (s.anzahl === 0) {
    wurzel.innerHTML = '<p class="leer">Keine Tauchgänge in dieser Auswahl. '
      + '<a href="#/statistik">Alle zeigen</a></p>';
    return wurzel;
  }

  const gefiltert = !istLeer({ ...kriterien });
  const stunden = Math.round(s.gesamtdauer / 3600);
  const offen = offeneBloecke();

  const { reisen, aufReise } = nachReise(auswahl);
  const reisekurz = reisen.length === 0
    ? 'keine Reise erfasst'
    : `${de.format(reisen.length)} ${reisen.length === 1 ? 'Reise' : 'Reisen'}`
      + ` · ${de.format(aufReise)} von ${de.format(s.anzahl)} Tauchgängen`;
  const reiseliste = reisen.length === 0
    ? '<p class="leer-leise">Kein Tauchgang dieser Auswahl gehört zu einer Reise.</p>'
    : `<ol class="rangliste">${reisen.map((r) => rangzeile(
      zuHash({ ...kriterien, reise: r.name }),
      r.name,
      [zeitraum(r.von, r.bis), `${r.tage} ${r.tage === 1 ? 'Tag' : 'Tage'}`,
        r.tiefste === null ? '' : `max ${tiefe(r.tiefste)}`].filter(Boolean).join(' · '),
      r.anzahl,
    )).join('')}</ol>`;

  const { buddys, mitBuddy } = nachBuddy(auswahl);
  const buddykurz = buddys.length === 0
    ? 'kein Buddy erfasst'
    : `${de.format(buddys.length)} ${buddys.length === 1 ? 'Buddy' : 'Buddys'}`
      + ` · ${buddys[0].name} (${buddys[0].anzahl}×)`;
  const buddyliste = buddys.length === 0
    ? '<p class="leer-leise">Kein Tauchgang dieser Auswahl nennt einen Buddy.</p>'
    : `<p class="klappe-stand">${de.format(mitBuddy)} von ${de.format(s.anzahl)} Tauchgängen`
      + ' nennen mindestens einen Buddy.</p>'
      + `<ol class="rangliste">${buddys.map((b) => rangzeile(
        zuHash({ ...kriterien, buddy: b.name }),
        b.name,
        jahresspanne(b.von, b.bis),
        b.anzahl,
      )).join('')}</ol>`;

  wurzel.innerHTML = `
    <header class="statistik-kopf">
      <h1>Statistik</h1>
      <p class="statistik-auswahl">${gefiltert
        ? `Auswahl: ${beschreibung(kriterien)} · <a href="#/statistik">alle ${alle.length}</a>`
        : `alle ${s.anzahl} Tauchgänge`}</p>
    </header>

    <div class="kennzahlen">
      ${kennzahl(de.format(s.anzahl), 'Tauchgänge',
    { zusatz: `${de.format(s.mitProfil)} mit Profil`, zeichen: 'liste' })}
      ${kennzahl(`${de.format(stunden)} h`, 'Grundzeit gesamt',
    { zusatz: `Ø ${dauer(s.schnittDauer)}`, zeichen: 'zeit' })}
      ${kennzahl(tiefe(s.schnittMaxTiefe), 'Ø Maximaltiefe', {
    zusatz: s.schnittTiefeVon
      ? `Ø Tiefe ${tiefe(s.schnittTiefe)} · ${s.schnittTiefeVon} erfasst` : '',
    zeichen: 'tiefe',
    farbe: typeof s.schnittMaxTiefe === 'number' ? tiefenfarbe(s.schnittMaxTiefe) : '',
  })}
      ${kennzahl(de.format(s.plaetze), 'Tauchplätze',
    { zusatz: `${s.laender.anzahl} ${s.laender.anzahl === 1 ? 'Land' : 'Länder'}`,
      zeichen: 'verorten' })}
    </div>

    <section class="block-weit">
      <h2>Tauchgänge pro Jahr</h2>
      ${jahresbalken(s.jahre, kriterien)}
    </section>

    <section class="block-weit">
      <h2>Tiefenverteilung <span class="leise">in Metern</span></h2>
      ${tiefenbalken(s.tiefen, kriterien)}
    </section>

    <div class="klappen">
      ${klappe('rekorde', 'Rekorde',
    `${tiefe(s.tiefster?.max_depth_m)} tiefster · ${dauer(s.laengster?.duration_s)} längster`, `
        <dl>
          <div class="feld"><dt>Tiefster Tauchgang</dt><dd class="dd-tiefster">${verweis(s.tiefster,
    tiefe(s.tiefster?.max_depth_m))}${s.tiefster?.needs_review
    ? ' <span class="marke">prüfen</span>' : ''}</dd></div>
          <div class="feld"><dt>Längster Tauchgang</dt><dd>${verweis(s.laengster,
    dauer(s.laengster?.duration_s))}</dd></div>
          <div class="feld"><dt>Kälteste Temperatur</dt><dd>${verweis(s.kaeltester,
    temperatur(s.kaelteste))}</dd></div>
          <div class="feld"><dt>Wärmste Temperatur</dt><dd>${verweis(s.waermster,
    temperatur(s.waermste))}</dd></div>
          <div class="feld"><dt>Erster Tauchgang</dt><dd>${(s.erster ?? '—').slice(0, 10)}</dd></div>
          <div class="feld"><dt>Letzter Tauchgang</dt><dd>${(s.letzter ?? '—').slice(0, 10)}</dd></div>
          ${s.zuPruefen ? `<div class="feld"><dt>Zu prüfen</dt><dd><a href="${
    zuHash({ ...kriterien, nurPruefen: true })}">${s.zuPruefen} Tauchgänge</a></dd></div>` : ''}
        </dl>`, offen.has('rekorde'), 'rekord')}

      ${klappe('reisen', 'Reisen', reisekurz, reiseliste, offen.has('reisen'), 'reise')}
      ${klappe('buddys', 'Buddys', buddykurz, buddyliste, offen.has('buddys'), 'buddy')}

      ${klappe('plaetze', 'Tauchplätze',
    `${de.format(s.plaetze)} Plätze${s.rangliste[0]
      ? ` · ${s.rangliste[0].name} (${s.rangliste[0].anzahl}×)` : ''}`, `
        <ol class="rangliste">
          ${s.rangliste.map((platz) => rangzeile(
    zuHash({ ...kriterien, platz: platz.name }), platz.name, '', platz.anzahl)).join('')}
        </ol>`, offen.has('plaetze'), 'verorten')}
    </div>`;

  // Der Griff haengt an einer eigenen Marke, nicht an der Reihenfolge im
  // Dokument: Sobald ein Block davor steht, traefe `.feld dd a` etwas anderes.
  const tiefsteKennzahl = wurzel.querySelector('.dd-tiefster a');
  if (tiefsteKennzahl && s.tiefster) {
    tiefsteKennzahl.style.color = tiefenfarbe(s.tiefster.max_depth_m);
  }
  wurzel.addEventListener('toggle', () => merkeOffen(wurzel), true);
  return wurzel;
}
